import { set } from "date-fns";
import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";

type CompareOption =
  | "equals"
  | "not_equals"
  | "less_than"
  | "less_than_equal"
  | "greater_than"
  | "greater_than_equal"
  | "between";

const compareOptions: { label: string; value: CompareOption }[] = [
  { label: "Equals", value: "equals" },
  { label: "Not equals", value: "not_equals" },
  { label: "Less than", value: "less_than" },
  { label: "Less than or equal to", value: "less_than_equal" },
  { label: "Greater than", value: "greater_than" },
  { label: "Greater than or equal to", value: "greater_than_equal" },
  { label: "Between", value: "between" },
];

interface SpendFilterModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedData?: any;
  onApply: (data: any) => void;
  type: string;
}

export const SpendFilterModal: React.FC<SpendFilterModalProps> = ({
  isOpen,
  onClose,
  onApply,
  selectedData,
  type,
}) => {
  const [compareType, setCompareType] = useState<CompareOption>("equals");
  const [compareWith, setCompareWith] = useState<"value" | "metric">("value");
  const [value, setValue] = useState("");
  const [minValue, setMinValue] = useState("");
  const [maxValue, setMaxValue] = useState("");
  const isBetween = compareType === "between";
  const [title, setTitle] = useState("");
  useEffect(() => {
    let localSelectedData: any;
    switch (type) {
      case "SPEND":
        setTitle("Spend");
        localSelectedData = selectedData.spend;
        break;
      case "SALES":
        setTitle("Sales");
        localSelectedData = selectedData.sales;
        break;
      case "ACOS":
        setTitle("ACOS");
        localSelectedData = selectedData.ACOS;
        break;
      case "CLICKS":
        setTitle("Clicks");
        localSelectedData = selectedData.clicks;
        break;
      case "DAILY_BUDGET":
        setTitle("Daily Budget");
        localSelectedData = selectedData.budget;
        break;
      case "IMPRESSIONS":
        setTitle("Impressions");
        localSelectedData = selectedData.impressions;
        break;
      case "ORDERS":
        setTitle("Orders");
        localSelectedData = selectedData.purchases;
        break;
      case "BID":
        setTitle("Bids");
        localSelectedData = selectedData.bid;
        break;
      case "CTR":
        setTitle("CTR");
        localSelectedData = selectedData.ctr;
        break;
      default:
        break;
    }
    setCompareType(localSelectedData?.compareType || "equals");
    setCompareWith(localSelectedData?.compareWith || "value");
    setValue(localSelectedData?.value || "");
    setMinValue(localSelectedData?.value?.min || "");
    setMaxValue(localSelectedData?.value?.max || "");
  }, [type, selectedData, isOpen]);

  const handleApply = () => {
    if (isBetween && (!minValue || !maxValue)) return;
    if (!isBetween && !value) return;

    const payload = {
      compareType,
      compareWith,
      value: isBetween ? { min: minValue, max: maxValue } : value,
      type: type,
    };

    onApply(payload);
    onClose();
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="modal fade show d-block" tabIndex={-1} role="dialog">
      <div className="modal-backdrop fade show" onClick={onClose}></div>
      <div
        className="modal-dialog modal-lg modal-dialog-centered"
        role="document"
        style={{ zIndex: 1050 }}
      >
        <div className="modal-content p-3">
          <div className="modal-header border-0">
            <h5 className="modal-title fw-bold">{title}</h5>
            <button
              type="button"
              className="btn-close"
              onClick={onClose}
            ></button>
          </div>

          {/* Horizontal layout */}
          <div className="modal-body d-flex flex-wrap align-items-center gap-3">
            {/* Radio */}
            {/* <div className="d-flex align-items-center me-3">
              <label className="me-2 fw-semibold">Compare With:</label>
              <div className="form-check form-check-inline">
                <input
                  className="form-check-input"
                  type="radio"
                  id="valueRadio"
                  checked={compareWith === 'value'}
                  onChange={() => setCompareWith('value')}
                />
                <label className="form-check-label" htmlFor="valueRadio">
                  Value
                </label>
              </div>
              <div className="form-check form-check-inline">
                <input
                  className="form-check-input"
                  type="radio"
                  id="metricRadio"
                  checked={compareWith === 'metric'}
                  onChange={() => setCompareWith('metric')}
                />
                <label className="form-check-label" htmlFor="metricRadio">
                  Metric
                </label>
              </div>
            </div> */}

            {/* Compare Dropdown */}
            <select
              className="form-select w-auto"
              value={compareType}
              onChange={(e) => setCompareType(e.target.value as CompareOption)}
            >
              {compareOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>

            {/* Input fields */}
            {isBetween ? (
              <div className="d-flex align-items-center gap-2">
                <input
                  className="form-control"
                  type="number"
                  placeholder="Min"
                  value={minValue}
                  onChange={(e) => setMinValue(e.target.value)}
                />
                <span className="fw-semibold">to</span>
                <input
                  className="form-control"
                  type="number"
                  placeholder="Max"
                  value={maxValue}
                  onChange={(e) => setMaxValue(e.target.value)}
                />
              </div>
            ) : (
              <div className="d-flex align-items-center gap-2">
                <input
                  className="form-control"
                  type="number"
                  placeholder="Value"
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                />
              </div>
            )}
          </div>

          {/* Footer buttons */}
          <div className="modal-footer border-0">
            <button className="btn btn-outline-secondary" onClick={onClose}>
              Cancel
            </button>
            <button style={{ height: "38px" }} className="btn btn-primary" onClick={handleApply}>
              Apply
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};
